import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import api from '../utils/api';

const contactStyles = {
  section: {
    padding: '60px 0 80px',
    background: 'linear-gradient(180deg, #FAF6EC 0%, #ffffff 100%)',
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: '28px',
    alignItems: 'start',
  },
  formsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  },
  formCard: {
    background: '#ffffff',
    borderRadius: '18px',
    padding: '32px 28px',
    border: '1px solid #E8E3D6',
    boxShadow: '0 10px 36px rgba(35,30,15,0.07)',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--dark-navy)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '13px 16px',
    fontSize: '15px',
    border: '1.5px solid #E8E3D6',
    borderRadius: '10px',
    background: '#FAF6EC',
    color: 'var(--body-text)',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
  },
  textarea: {
    width: '100%',
    padding: '13px 16px',
    fontSize: '15px',
    border: '1.5px solid #E8E3D6',
    borderRadius: '10px',
    background: '#FAF6EC',
    color: 'var(--body-text)',
    outline: 'none',
    minHeight: '110px',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
  },
  formTitle: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: '28px',
    fontWeight: 400,
    color: 'var(--dark-navy)',
    lineHeight: 1.2,
    marginBottom: '8px',
  },
  formSubtitle: {
    fontSize: '15px',
    color: 'var(--body-text)',
    marginBottom: '20px',
    lineHeight: 1.6,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    position: 'sticky',
    top: '100px',
  },
  infoCard: {
    background: '#ffffff',
    borderRadius: '18px',
    padding: '32px 28px',
    border: '1px solid #E8E3D6',
    boxShadow: '0 10px 36px rgba(35,30,15,0.07)',
  },
  infoTitle: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: '28px',
    fontWeight: 400,
    color: 'var(--dark-navy)',
    marginBottom: '20px',
  },
  infoItem: {
    display: 'grid',
    gridTemplateColumns: '48px 1fr',
    gap: '14px',
    alignItems: 'start',
    marginBottom: '18px',
  },
  infoIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'var(--blue)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 700,
  },
  infoLabel: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#8da0bd',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  infoText: {
    fontSize: '16px',
    color: 'var(--dark-navy)',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  infoHighlight: {
    marginTop: '20px',
    padding: '16px 18px',
    borderRadius: '12px',
    border: '1px solid #cfeee1',
    background: '#effbf5',
  },
  infoHighlightTitle: {
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#2f6f58',
    marginBottom: '6px',
  },
  infoHighlightText: {
    fontSize: '15px',
    color: '#214536',
    lineHeight: 1.5,
  },
  tipsCard: {
    background: '#ffffff',
    borderRadius: '18px',
    padding: '32px 28px',
    border: '1px solid #E8E3D6',
    boxShadow: '0 10px 36px rgba(35,30,15,0.07)',
    display: 'flex',
    flexDirection: 'column',
  },
  tipsTitle: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: '28px',
    fontWeight: 400,
    color: 'var(--dark-navy)',
    marginBottom: '16px',
  },
  tipsSubtitle: {
    fontSize: '15px',
    color: 'var(--body-text)',
    marginBottom: '20px',
    lineHeight: 1.6,
  },
  tipsList: {
    margin: 0,
    paddingLeft: '20px',
    flex: 1,
  },
  tipsItem: {
    fontSize: '15px',
    color: 'var(--body-text)',
    lineHeight: 1.7,
    marginBottom: '10px',
  },
  tipsHighlight: {
    marginTop: '20px',
    padding: '16px 18px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)',
    color: '#fff',
    textAlign: 'center',
  },
  tipsHighlightText: {
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  successMsg: {
    background: '#E8F5F6',
    color: '#338F99',
    padding: '14px 20px',
    borderRadius: 'var(--radius-sm)',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid #b5e8d0',
  },
};

const INITIAL_FORM = {
  fullName: '',
  email: '',
  age: '',
  phone: '',
  queryIssue: '',
  message: '',
};

function ContactPage() {
  const [therapyForm, setTherapyForm] = useState(INITIAL_FORM);
  const [courseForm, setCourseForm] = useState(INITIAL_FORM);
  const [therapyState, setTherapyState] = useState({ status: 'idle', msg: '' });
  const [courseState, setCourseState] = useState({ status: 'idle', msg: '' });
  const [searchParams] = useSearchParams();

  // Prefill the General Enquiry "Query / Issue" field when arriving from the
  // popup's enquiry CTA (which appends ?query=<popup title>).
  useEffect(() => {
    const query = (searchParams.get('query') || '').trim();
    if (!query) return;
    setCourseForm((prev) => (prev.queryIssue ? prev : { ...prev, queryIssue: query }));
  }, [searchParams]);

  const handleChange = (e, setFormData) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // kind: 'enquiry' for the Course Enquiry form, 'appointment' for Book Therapy Session
  const handleSubmit = async (e, formData, setFormData, setState, kind, topicLabel) => {
    e.preventDefault();
    setState({ status: 'sending', msg: '' });
    try {
      const fullName = (formData.fullName || '').trim();
      const [firstName, ...rest] = fullName.split(/\s+/);
      const lastName = rest.join(' ');
      const lines = [];
      if (formData.age) lines.push(`Age: ${formData.age}`);
      if (formData.queryIssue) lines.push(`Query: ${formData.queryIssue}`);
      if (formData.preference) lines.push(`Preference: ${formData.preference}`);
      if (formData.timing) lines.push(`Preferred time: ${formData.timing}`);
      if (formData.message) lines.push('', formData.message);
      const message = lines.join('\n') || formData.message || `${topicLabel} request from website.`;

      if (kind === 'appointment') {
        const res = await api.submitAppointment({
          firstName: firstName || fullName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          message,
        });
        setState({ status: 'ok', msg: res?.message || "Thanks — we'll be in touch shortly." });
      } else {
        const res = await api.submitEnquiry({
          name: fullName,
          email: formData.email,
          phone: formData.phone,
          topic: topicLabel,
          message,
        });
        setState({ status: 'ok', msg: res?.message || "Thanks — we'll be in touch shortly." });
      }
      setFormData(INITIAL_FORM);
    } catch (err) {
      setState({
        status: 'error',
        msg: err.message || "Couldn't send your request. Please try WhatsApp instead.",
      });
    }
  };

  const renderForm = ({ title, subtitle, formData, setFormData, state, setState, buttonLabel, phoneLabel = 'Phone Number', kind, topicLabel }) => (
    <div style={contactStyles.formCard}>
      <h2 style={contactStyles.formTitle}>{title}</h2>
      <p style={contactStyles.formSubtitle}>{subtitle}</p>

      {state.status === 'ok' && (
        <div style={contactStyles.successMsg}>{state.msg}</div>
      )}
      {state.status === 'error' && (
        <div style={{ ...contactStyles.successMsg, background: '#FBE9E8', color: '#B0413E', borderColor: '#B0413E' }}>
          {state.msg}
        </div>
      )}

      <form onSubmit={(e) => handleSubmit(e, formData, setFormData, setState, kind, topicLabel)}>
        <div className="contact-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={contactStyles.formGroup}>
            <label style={contactStyles.label}>Full Name *</label>
            <input
              style={contactStyles.input}
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Your full name"
              required
            />
          </div>
          <div style={contactStyles.formGroup}>
            <label style={contactStyles.label}>Email *</label>
            <input
              style={contactStyles.input}
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="your@email.com"
              required
            />
          </div>
        </div>

        <div className="contact-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={contactStyles.formGroup}>
            <label style={contactStyles.label}>Age *</label>
            <input
              style={contactStyles.input}
              type="number"
              name="age"
              value={formData.age}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Your age"
              min="1"
              required
            />
          </div>
          <div style={contactStyles.formGroup}>
            <label style={contactStyles.label}>{phoneLabel} *</label>
            <input
              style={contactStyles.input}
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="+91 XXXXX XXXXX"
              required
            />
          </div>
        </div>

        <div style={contactStyles.formGroup}>
          <label style={contactStyles.label}>Query / Issue *</label>
          <input
            style={contactStyles.input}
            type="text"
            name="queryIssue"
            value={formData.queryIssue}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Write your query or issue"
            required
          />
        </div>

        <div style={contactStyles.formGroup}>
          <label style={contactStyles.label}>Message *</label>
          <textarea
            style={contactStyles.textarea}
            name="message"
            value={formData.message}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Write your message here..."
            required
          />
        </div>

        <button
          type="submit"
          className="btn-primary"
          style={{ marginTop: '8px' }}
          disabled={state.status === 'sending'}
        >
          {state.status === 'sending' ? 'Sending…' : buttonLabel}
        </button>
      </form>
    </div>
  );

  return (
    <>
      <PageBanner title="Contact Us" />

      <section style={contactStyles.section}>
        <div className="container">
          <div style={contactStyles.mainGrid} className="contact-main-grid">
            {/* Left: Forms stacked */}
            <div style={contactStyles.formsColumn}>
              {renderForm({
                title: 'General Enquiry',
                subtitle: 'Questions about our courses, sessions, or anything else? Get in touch.',
                formData: courseForm,
                setFormData: setCourseForm,
                state: courseState,
                setState: setCourseState,
                buttonLabel: 'Submit Enquiry',
                phoneLabel: 'WhatsApp Number',
                kind: 'enquiry',
                topicLabel: 'General Enquiry',
              })}

              {renderForm({
                title: 'Book a Therapy Session',
                subtitle: 'Fill in your details to schedule a Hypnotherapy or Psychotherapy session.',
                formData: therapyForm,
                setFormData: setTherapyForm,
                state: therapyState,
                setState: setTherapyState,
                buttonLabel: 'Book Session',
                kind: 'appointment',
                topicLabel: 'Therapy Session',
              })}
            </div>

            {/* Right: Contact info + Tips */}
            <div style={contactStyles.rightColumn} className="contact-right-col">
              <div style={contactStyles.infoCard}>
                <h2 style={contactStyles.infoTitle}>Contact Information</h2>

                <div style={contactStyles.infoItem}>
                  <div style={contactStyles.infoIcon}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div>
                    <p style={contactStyles.infoLabel}>Address</p>
                    <p style={contactStyles.infoText}>Lucknow, Uttar Pradesh, India</p>
                  </div>
                </div>

                <div style={contactStyles.infoItem}>
                  <div style={contactStyles.infoIcon}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.81.36 1.6.7 2.35a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.75.34 1.54.57 2.35.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </div>
                  <div>
                    <p style={contactStyles.infoLabel}>Phone</p>
                    <p style={contactStyles.infoText}>+91 9140395816<br />+91 8840676202</p>
                  </div>
                </div>

                <div style={contactStyles.infoItem}>
                  <div style={contactStyles.infoIcon}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <path d="M22 6l-10 7L2 6"/>
                    </svg>
                  </div>
                  <div>
                    <p style={contactStyles.infoLabel}>Email</p>
                    <p style={contactStyles.infoText}>mindspalko@gmail.com</p>
                  </div>
                </div>

                <div style={{ ...contactStyles.infoItem, marginBottom: 0 }}>
                  <div style={contactStyles.infoIcon}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div>
                    <p style={contactStyles.infoLabel}>Working Hours</p>
                    <p style={contactStyles.infoText}>On Appointment Only</p>
                  </div>
                </div>

                <div style={contactStyles.infoHighlight}>
                  <p style={contactStyles.infoHighlightTitle}>Quick Response</p>
                  <p style={contactStyles.infoHighlightText}>
                    We usually reply within 24 hours. For faster support, please call the phone numbers above.
                  </p>
                </div>
              </div>

              <div style={contactStyles.tipsCard}>
                <h3 style={contactStyles.tipsTitle}>Before You Book</h3>
                <p style={contactStyles.tipsSubtitle}>A few things to keep in mind when reaching out to us.</p>
                <ul style={contactStyles.tipsList}>
                  <li style={contactStyles.tipsItem}>Choose your preferred date and time in the message.</li>
                  <li style={contactStyles.tipsItem}>Mention your main concern for better guidance.</li>
                  <li style={contactStyles.tipsItem}>Online and in-person sessions are available.</li>
                  <li style={{ ...contactStyles.tipsItem, marginBottom: 0 }}>Average response time: within 24 hours.</li>
                </ul>
                <div style={contactStyles.tipsHighlight}>
                  <p style={contactStyles.tipsHighlightText}>
                    Need urgent help? Call us directly at <strong>+91 9140395816</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .contact-main-grid input:focus,
        .contact-main-grid textarea:focus {
          border-color: var(--blue);
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(63, 169, 181, 0.13);
        }

        @media (max-width: 960px) {
          .contact-main-grid {
            grid-template-columns: 1fr !important;
          }
          .contact-right-col {
            position: static !important;
          }
        }
        @media (max-width: 600px) {
          .contact-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}

export default ContactPage;
